/*
 * Copyright (c) Worldline 2022.
 */

import {Component, OnInit} from '@angular/core';
import {ExternalSessionStatus} from "../../constant/external-session-status";
import {CustomErrors} from "../../constant/custom-errors";
import {NgxSpinnerService} from "ngx-spinner";
import {TranslateService} from "@ngx-translate/core";
import {Errors} from "../../constant/errors";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {SessionService} from "../../services/session.service";
import {RedirectionService} from "../../services/redirection.service";
import {SignerService} from "../../services/signer.service";
import {HttpService} from "../../http/http.service";
import {CancellationReasonDto} from "../../model/cancellationReasonDto";

@Component({
  selector: 'app-confirm-cancellation',
  templateUrl: './confirm-cancellation.component.html'
})
export class ConfirmCancellationComponent implements OnInit {

  constructor(private route: ActivatedRoute,
              private sessionService: SessionService,
              private signerService: SignerService,
              private redirectionService: RedirectionService,
              private spinnerService: NgxSpinnerService,
              private translateService: TranslateService,
              private formBuilder: FormBuilder,
              private http: HttpService) {
    this.formValidation = this.formBuilder.group({
      reason: [],
      detail: []
    });
  }

  detail: any;
  specificRequired: any;
  customRequired: any;
  nothingRequired: any;
  specificData: any
  onHoldAvailability: any;
  onHoldLimitReached: any;
  errorMsg: any;
  showCancelBox: any;
  formValidation: FormGroup;
  maxlength: any;

  private sessionId: any;
  private strategy: any;

  ngOnInit(): void {

    // Check the sessionId
    this.sessionId = this.route.snapshot.params['sessionId'];

    if (this.sessionId !== this.sessionService.id) {
      console.warn('The session ID in the url is not the same as the saved : url->' + this.sessionId + ' | saved->' + this.sessionService.id);
      this.redirectionService.session(this.route, this.sessionId);
      return;
    }

    // Check the status of the session
    if (typeof this.sessionService.info === 'undefined' || this.sessionService.info.externalSessionStatus !== ExternalSessionStatus.init) {
      console.warn('The current signer is undefined or is not at the init status');
      this.redirectionService.session(this.route, this.sessionId);
      return;
    }

    //Get cancellation strategy
    this.strategy = this.sessionService.info.configDto.cancellationStrategyDto.strategy;

    this.specificRequired = (this.strategy === 'SPECIFIC_REASON' || this.strategy === 'SPECIFIC_DETAIL_REASON');
    this.customRequired = (this.strategy === 'CUSTOM_REASON' || this.strategy === 'SPECIFIC_DETAIL_REASON');
    this.nothingRequired = (this.strategy === 'NO_REASON');

    if (this.specificRequired) {
      //Get reasons
      this.specificData = {
        model: '',
        reasons: this.sessionService.info.configDto.cancellationStrategyDto.reasons
      };
      this.formValidation.get('reason')?.setValidators([Validators.required])
    }

    if (this.customRequired) {
      this.detail = null;
      //max character size for text area
      this.maxlength = 255;
      this.formValidation.get('detail')?.setValidators([Validators.required, Validators.maxLength(this.maxlength), Validators.minLength(1)])
    }

    //on hold availability
    this.onHoldAvailability = this.signerService.currentSigner.signingMode !== 'FACE_TO_FACE' && this.sessionService.info.configDto.onHoldAvailability;
    this.onHoldLimitReached = this.onHoldAvailability && this.sessionService.info.onHoldCounter >= this.sessionService.info.configDto.onHoldMaxLimit;
  }

  // return (forgive cancel action)
  back() {
    var sessionId = this.route.snapshot.params['sessionId'];
    this.redirectionService.session(this.route, sessionId);
  };

  goTo(state) {
    const that = this;

    var sessionId = this.route.snapshot.params['sessionId'];
    var signerId = this.signerService.currentSigner.extSignerId;

    if (state === 'hold') {
      console.debug('Hold the session');
      // Enable spin
      this.spinnerService.show('spinner');

      this.http.hold(sessionId, signerId).toPromise()
          .then(function (response) {

            //Disable the spin
            that.spinnerService.hide('spinner');

            if (response?.success) {
              console.debug('Hold the session : SUCCESS');
              var urlOnHold = that.sessionService.info.urlOnHold;
              if (urlOnHold) {
                that.redirectionService.exit(that.sessionService.getUrlOnHold());
              } else {
                that.redirectionService.hold(that.route, sessionId);
              }

            } else {
              //Log the error and redirect
              console.warn(response?.error);
              that.errorMsg = 'error.unexpected';
            }
          }, that.exitError);
    } else if (state === 'cancel') {
      that.showCancelBox = true;
    } else if (state === 'continue') {
      that.redirectionService.session(this.route, sessionId);
    } else {
      that.showCancelBox = false;
    }
  };

  canCancel() {
    if (this.nothingRequired) {
      return true;
    }

    if (this.specificRequired) {
      for (var i = 0; i < this.specificData.reasons.length; i++) {
        if (this.specificData.reasons[i] === this.specificData.model) {
          return true;
        }
      }
      return false;
    }

    if (this.customRequired) {
      if (!this.detail) {
        return false;
      }
      if ((this.remaining() >= 0) && (this.remaining() < this.maxlength)) {
        return true;
      } else {
        return false;
      }
    }
    return;
  };

  goToContact() {
    this.redirectionService.internalContact(this.route, this.sessionId);
  };

  //  irrevocable cancellation...
  cancel(form: FormGroup) {
    const that = this;
    if (this.canCancel()) {
      console.debug('Canceling the session');

      if (!form.valid) {
        return;
      }

      // Enable spin
      this.spinnerService.show('spinner');

      var sessionId = this.route.snapshot.params['sessionId'];
      var signerId = this.signerService.currentSigner.extSignerId;

      console.debug('cancel for session ' + sessionId + ' and signer ' + signerId);

      if (this.customRequired && !this.specificRequired) {
        if (this.detail === '') {
          this.detail = null;
        }
      } else if (this.specificRequired && this.specificData.model === 'cancel.reason.alternativeway') {
        this.detail = this.translateService.instant(this.specificData.model);
      } else {
        this.detail = null;
      }

      var specific = null;
      if (this.specificRequired) {
        specific = this.specificData.model;
      }

      var dto: CancellationReasonDto = {
        reason: specific ? specific : undefined,
        detail: this.detail
      };

      this.http.cancel(sessionId, signerId, dto).toPromise()
          .then(function (response) {

            //Disable the spin
            that.spinnerService.show('spinner');

            if (response?.success) {
              console.debug('Canceling the session : SUCCESS');
              that.redirectionService.session(that.route, sessionId);
            } else {
              //Log the error and redirect
              console.warn(response?.error);
              switch (response?.error?.errorCode) {
                case Errors.cancelReasonNotFound:
                case Errors.cancelReasonNotValid:
                  that.errorMsg = 'cancellation.reason.required';
                  break;
                default:
                  that.errorMsg = 'error.unexpected';
                  break;
              }
            }
          }, that.exitError);
    }
  };

  remaining() {
    if (this.detail) {
      return this.maxlength - this.detail.length;
    } else {
      return this.maxlength;
    }
  };

  private exitError() {
    this.redirectionService.error(this.route, CustomErrors.unreachableService, this.sessionId);
  }

}
